import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ruloHesaplamaNew from './assets/rulo_hesaplama_new.png';
import { LOCALES } from './i18n';
import pulEbat from './assets/pul_ebat.png';
import pul1 from './assets/pul1.png';
import pul2 from './assets/pul2.png';
import pul3 from './assets/pul3.png';
import pul4 from './assets/pul4.png';

// Dizilim görselleri (key → görsel)
const LAYOUT_SVGS = {
    tek: <img src={pul1} alt="" className="max-h-40 w-auto object-contain" />,
    cift: <img src={pul2} alt="" className="max-h-40 w-auto object-contain" />,
    uclu: <img src={pul3} alt="" className="max-h-40 w-auto object-contain" />,
    dortlu: <img src={pul4} alt="" className="max-h-40 w-auto object-contain" />,
};

const INCH_TO_MM = 25.4;
const KG_TO_LB = 2.2046226218;
const KG_M3_TO_LB_IN3 = 0.000036127292;

function NumericInput({ value, onChange, disabled = false }) {
    const adjustValue = (delta) => {
        const normalized = String(value ?? "").trim().replace(/\s+/g, "").replace(",", ".");
        const current = Number(normalized);
        const next = (Number.isFinite(current) ? current : 0) + delta;
        onChange(String(Number(next.toFixed(4))));
    };

    return (
        <div className="relative w-40 sm:w-48">
            <div className="absolute left-1 top-1/2 z-10 grid -translate-y-1/2 grid-cols-2 gap-0.5">
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => adjustValue(1)}
                    className="h-7 w-5 rounded-md border border-gray-300 bg-gray-50 text-xs font-black text-gray-600 disabled:cursor-not-allowed disabled:text-gray-300"
                    aria-label="Artır"
                >
                    +
                </button>
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => adjustValue(-1)}
                    className="h-7 w-5 rounded-md border border-gray-300 bg-gray-50 text-xs font-black text-gray-600 disabled:cursor-not-allowed disabled:text-gray-300"
                    aria-label="Azalt"
                >
                    -
                </button>
            </div>
            <input
                inputMode="decimal"
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                className="h-10 w-40 rounded-lg border-2 border-gray-200 bg-white pl-12 pr-8 text-right font-semibold text-gray-800 transition-colors focus:border-blue-400 focus:outline-none disabled:bg-gray-50 disabled:text-gray-500 sm:w-48"
            />
        </div>
    );
}

const parseNumber = (value) => {
    const normalized = String(value ?? "").trim().replace(/\s+/g, "").replace(",", ".");
    const number = Number(normalized);
    return Number.isFinite(number) ? number : NaN;
};

const ZigzagCalculator = ({ t, lang, unitSystem = "metric" }) => {
    const MATERIALS = useMemo(
        () => [
            { key: "steel", name: t.matSteel, density: 7850 },
            { key: "stainless", name: t.matStainless, density: 8000 },
            { key: "aluminum", name: t.matAluminum, density: 2700 },
            { key: "copper", name: t.matCopper, density: 8960 },
            { key: "brass", name: t.matBrass, density: 8500 },
            { key: "cast_iron", name: t.matCastIron, density: 7200 },
            { key: "pp", name: t.matPP, density: 905 },
            { key: "pe", name: t.matPE, density: 950 },
            { key: "pvc", name: t.matPVC, density: 1380 },
            { key: "custom", name: t.matCustom, density: null },
        ],
        [t]
    );

    const [materialKey, setMaterialKey] = useState('');
    const previousUnitSystem = useRef(unitSystem);

    // Pul özellikleri state - boş başlar, kullanıcı girer
    const [pulOzellikleri, setPulOzellikleri] = useState({
        diameter: '',       // Ø (mm)
        thickness: '',      // t (mm)
        quantity: '',       // Adet
        density: ''         // Yoğunluk (kg/m³)
    });

    // Rulo özellikleri - sadece standart ID değerleri seçilebilir
    const [ruloIcCap, setRuloIcCap] = useState('508'); // ID (mm)

    const selectedMaterial = MATERIALS.find((m) => m.key === materialKey);
    const isDensityLocked =
        selectedMaterial && selectedMaterial.key && selectedMaterial.key !== "custom";
    const isImperial = unitSystem === "imperial";
    const lengthUnit = isImperial ? "in" : "mm";
    const weightUnit = isImperial ? "lb" : "kg";
    const densityUnit = isImperial ? "lb/in³" : "kg/m³";
    const fromMetricLength = useCallback((value) => isImperial ? value / INCH_TO_MM : value, [isImperial]);
    const toMetricLength = useCallback((value) => isImperial ? value * INCH_TO_MM : value, [isImperial]);
    const fromMetricWeight = useCallback((value) => isImperial ? value * KG_TO_LB : value, [isImperial]);
    const fromMetricDensity = useCallback((value) => isImperial ? value * KG_M3_TO_LB_IN3 : value, [isImperial]);
    const toMetricDensity = useCallback((value) => isImperial ? value / KG_M3_TO_LB_IN3 : value, [isImperial]);

    // Hesaplamalar
    const calculations = useMemo(() => {
        const d = toMetricLength(parseNumber(pulOzellikleri.diameter));
        const t = toMetricLength(parseNumber(pulOzellikleri.thickness));
        const n = parseNumber(pulOzellikleri.quantity);
        const rho = toMetricDensity(parseNumber(pulOzellikleri.density));
        const dIc = parseNumber(ruloIcCap);

        // Parça ağırlığı (tüm dizilimler için aynı)
        const parcaAgirlik = Math.PI * Math.pow(d, 2) / 4 * t * rho * Math.pow(10, -9);

        // Tek dizilim
        const tekW = d;
        const tekToplamBoy = d * n;
        const tekAgirlik = tekToplamBoy * t * rho * tekW * Math.pow(10, -9);
        const tekOD = Math.sqrt(tekToplamBoy * 4 * t / Math.PI + Math.pow(dIc, 2));
        const tekPresTabla = tekW * 2 - d;
        const tekFire = (tekAgirlik - parcaAgirlik * n) / n;
        const tekAvantaj = 0;

        // Çift dizilim (zigzag)
        const ciftW = d + d * Math.sqrt(3) / 2;
        const ciftToplamBoy = n % 2 === 0
            ? (n / 2 + 0.5) * d
            : ((n - 1) / 2 + 1) * d;
        const ciftAgirlik = ciftToplamBoy * t * rho * ciftW * Math.pow(10, -9);
        const ciftOD = Math.sqrt(ciftToplamBoy * 4 * t / Math.PI + Math.pow(dIc, 2));
        const ciftPresTabla = ciftW * 2 - d;
        const ciftFire = (ciftAgirlik - parcaAgirlik * n) / n;
        const ciftAvantaj = ((parcaAgirlik + tekFire) - (parcaAgirlik + ciftFire)) / (parcaAgirlik + tekFire);

        // Üçlü dizilim
        const ucluW = d + d * Math.sqrt(3);
        const ucluToplamBoy = d + d / 2 + (n % 3 === 0
            ? ((n - 3) / 3) * d
            : Math.floor((n - 3) / 3) * d + d / 2);
        const ucluAgirlik = ucluToplamBoy * t * rho * ucluW * Math.pow(10, -9);
        const ucluOD = Math.sqrt(ucluToplamBoy * 4 * t / Math.PI + Math.pow(dIc, 2));
        const ucluPresTabla = ucluW * 2 - d;
        const ucluFire = (ucluAgirlik - parcaAgirlik * n) / n;
        const ucluAvantaj = ((parcaAgirlik + tekFire) - (parcaAgirlik + ucluFire)) / (parcaAgirlik + tekFire);

        // Dörtlü dizilim
        const dortluW = d + d * Math.sqrt(3) * 3 / 2;
        const dortluToplamBoy = d + d / 2 + (n % 2 === 0
            ? (n - 4) / 2 * d / 2
            : (n - 3) / 2 * d / 2);
        const dortluAgirlik = dortluToplamBoy * t * rho * dortluW * Math.pow(10, -9);
        const dortluOD = Math.sqrt(dortluToplamBoy * 4 * t / Math.PI + Math.pow(dIc, 2));
        const dortluPresTabla = dortluW * 2 - d;
        const dortluFire = (dortluAgirlik - parcaAgirlik * n) / n;
        const dortluAvantaj = ((parcaAgirlik + tekFire) - (parcaAgirlik + dortluFire)) / (parcaAgirlik + tekFire);

        return {
            parcaAgirlik,
            tek: {
                agirlik: tekAgirlik,
                OD: tekOD,
                Od: dIc,
                W: tekW,
                presTabla: tekPresTabla,
                toplamBoy: tekToplamBoy,
                fire: tekFire,
                avantaj: tekAvantaj
            },
            cift: {
                agirlik: ciftAgirlik,
                OD: ciftOD,
                Od: dIc,
                W: ciftW,
                presTabla: ciftPresTabla,
                toplamBoy: ciftToplamBoy,
                fire: ciftFire,
                avantaj: ciftAvantaj
            },
            uclu: {
                agirlik: ucluAgirlik,
                OD: ucluOD,
                Od: dIc,
                W: ucluW,
                presTabla: ucluPresTabla,
                toplamBoy: ucluToplamBoy,
                fire: ucluFire,
                avantaj: ucluAvantaj
            },
            dortlu: {
                agirlik: dortluAgirlik,
                OD: dortluOD,
                Od: dIc,
                W: dortluW,
                presTabla: dortluPresTabla,
                toplamBoy: dortluToplamBoy,
                fire: dortluFire,
                avantaj: dortluAvantaj
            }
        };
    }, [pulOzellikleri, ruloIcCap, toMetricDensity, toMetricLength]);

    const formatNumber = useCallback((num, decimals = 2) => {
        if (isNaN(num) || !isFinite(num)) return '-';
        return num.toLocaleString(LOCALES[lang] || 'tr-TR', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    }, [lang]);

    const formatPercent = (num) => {
        if (isNaN(num) || !isFinite(num)) return '-';
        return (num * 100).toFixed(1) + '%';
    };

    const handleInputChange = (field, value) => {
        setPulOzellikleri(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleMaterialChange = (key) => {
        setMaterialKey(key);

        const material = MATERIALS.find((m) => m.key === key);
        if (!material) return;

        handleInputChange('density', material.key === 'custom' ? '' : formatNumber(fromMetricDensity(material.density), isImperial ? 4 : 0));
    };

    const convertDisplayValue = useCallback((value, converter, decimals = 4) => {
        const number = parseNumber(value);
        return Number.isFinite(number) ? formatNumber(converter(number), decimals) : value;
    }, [formatNumber]);

    useEffect(() => {
        const previous = previousUnitSystem.current;
        if (previous === unitSystem) return;

        const wasImperial = previous === "imperial";
        const fromPreviousLength = (value) => wasImperial ? value * INCH_TO_MM : value;
        const toCurrentLength = (value) => isImperial ? value / INCH_TO_MM : value;
        const fromPreviousDensity = (value) => wasImperial ? value / KG_M3_TO_LB_IN3 : value;
        const toCurrentDensity = (value) => isImperial ? value * KG_M3_TO_LB_IN3 : value;

        setPulOzellikleri((prev) => ({
            ...prev,
            diameter: convertDisplayValue(prev.diameter, (n) => toCurrentLength(fromPreviousLength(n)), isImperial ? 3 : 2),
            thickness: convertDisplayValue(prev.thickness, (n) => toCurrentLength(fromPreviousLength(n)), isImperial ? 3 : 2),
            density: prev.density,
        }));

        const material = MATERIALS.find((m) => m.key === materialKey);
        if (material && material.key !== "custom") {
            handleInputChange('density', formatNumber(toCurrentDensity(material.density), isImperial ? 4 : 0));
        } else {
            setPulOzellikleri((prev) => ({
                ...prev,
                density: convertDisplayValue(prev.density, (n) => toCurrentDensity(fromPreviousDensity(n)), isImperial ? 4 : 0),
            }));
        }

        previousUnitSystem.current = unitSystem;
    }, [MATERIALS, convertDisplayValue, formatNumber, isImperial, materialKey, unitSystem]);

    const fixedLayouts = useMemo(() => [
        { key: 'tek', label: t.single, avantaj: calculations.tek.avantaj },
        { key: 'cift', label: t.doubleZigzag, avantaj: calculations.cift.avantaj },
        { key: 'uclu', label: t.triple, avantaj: calculations.uclu.avantaj },
        { key: 'dortlu', label: t.quad, avantaj: calculations.dortlu.avantaj },
    ], [calculations, t]);

    const hasLayoutData = fixedLayouts.every(i => Number.isFinite(i.avantaj));
    const bestLayoutKey = hasLayoutData
        ? fixedLayouts.reduce((best, item) => item.avantaj > best.avantaj ? item : best, fixedLayouts[0]).key
        : null;

    return (
        <div className="max-w-6xl mx-auto">
            {/* Pul Özellikleri Section */}
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6">
                {/* Başlık - rulo hesaplayıcıdaki gibi kart içinde küçük */}
                <div className="text-base font-bold text-gray-800 mb-3">
                    {t.tabZigzag}
                    <span className="ml-2 text-xs font-medium text-gray-400">{t.zzSubtitle}</span>
                </div>
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-stretch lg:items-center">
                    {/* Sol taraf - Label ve Inputlar */}
                    <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
                        <div className="text-center sm:flex-shrink-0">
                            <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center shadow-inner">
                                <span className="text-sm font-semibold text-gray-600">{t.discProps}</span>
                            </div>
                        </div>

                        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                            <div className="flex items-center justify-between gap-2 sm:col-span-2 sm:justify-start sm:gap-3">
                                <label className="w-28 flex-shrink-0 rounded-lg bg-gray-100 px-3 py-2 text-center text-sm font-medium text-gray-700 sm:w-36 sm:px-4">
                                    {t.material}
                                </label>
                                <select
                                    value={materialKey}
                                    onChange={(e) => handleMaterialChange(e.target.value)}
                                    className="h-10 w-40 rounded-lg border-2 border-gray-200 bg-white px-3 text-sm font-semibold text-gray-800 focus:border-blue-400 focus:outline-none sm:w-72"
                                >
                                    <option value="" disabled>{t.select}</option>
                                    {MATERIALS.map((material) => (
                                        <option key={material.key} value={material.key}>
                                            {material.name}
                                            {material.density ? ` (${formatNumber(fromMetricDensity(material.density), isImperial ? 4 : 0)})` : ""}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex items-center justify-between gap-2 sm:justify-start sm:gap-3">
                                <label className="w-28 flex-shrink-0 rounded-lg bg-gray-100 px-3 py-2 text-center text-sm font-medium text-gray-700 sm:w-36 sm:px-4">
                                    Ø ({lengthUnit})
                                </label>
                                <NumericInput
                                    value={pulOzellikleri.diameter}
                                    onChange={(value) => handleInputChange('diameter', value)}
                                />
                            </div>
                            <div className="flex items-center justify-between gap-2 sm:justify-start sm:gap-3">
                                <label className="w-28 flex-shrink-0 rounded-lg bg-gray-100 px-3 py-2 text-center text-sm font-medium text-gray-700 sm:w-36 sm:px-4">
                                    t ({lengthUnit})
                                </label>
                                <NumericInput
                                    value={pulOzellikleri.thickness}
                                    onChange={(value) => handleInputChange('thickness', value)}
                                />
                            </div>
                            <div className="flex items-center justify-between gap-2 sm:justify-start sm:gap-3">
                                <label className="w-28 flex-shrink-0 rounded-lg bg-gray-100 px-3 py-2 text-center text-sm font-medium text-gray-700 sm:w-36 sm:px-4">
                                    {t.quantity}
                                </label>
                                <NumericInput
                                    value={pulOzellikleri.quantity}
                                    onChange={(value) => handleInputChange('quantity', value)}
                                />
                            </div>
                            <div className="flex items-center justify-between gap-2 sm:col-span-2 sm:justify-start sm:gap-3">
                                <label className="w-28 flex-shrink-0 rounded-lg bg-gray-100 px-3 py-2 text-center text-sm font-medium text-gray-700 sm:w-36 sm:px-4">
                                    {t.density} ({densityUnit})
                                </label>
                                <NumericInput
                                    value={pulOzellikleri.density}
                                    disabled={isDensityLocked}
                                    onChange={(value) => handleInputChange('density', value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Sağ taraf - Pul Görseli */}
                    <div className="flex flex-shrink-0 justify-center">
                      <img src={pulEbat} alt="" className="w-40 sm:w-[200px]"/>
                    </div>
                </div>

                {/* Rulo Özellikleri - iç çap girişi */}
                <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
                    <div className="text-center sm:flex-shrink-0">
                        <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center shadow-inner">
                            <span className="text-sm font-semibold text-gray-600">{t.coilProps}</span>
                        </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 sm:justify-start sm:gap-3">
                        <label className="w-28 flex-shrink-0 rounded-lg bg-gray-100 px-3 py-2 text-center text-sm font-medium text-gray-700 sm:w-36 sm:px-4">
                            ID ({lengthUnit})
                        </label>
                        <select
                            value={ruloIcCap}
                            onChange={(e) => setRuloIcCap(e.target.value)}
                            className="h-10 w-40 rounded-lg border-2 border-gray-200 bg-white px-4 text-right font-semibold text-gray-800 focus:border-blue-400 focus:outline-none sm:w-48"
                        >
                            <option value="508">{formatNumber(fromMetricLength(508), isImperial ? 3 : 0)}</option>
                            <option value="610">{formatNumber(fromMetricLength(610), isImperial ? 3 : 0)}</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Rulo Özellikleri ve Sonuçlar Tablosu */}
            <div className="bg-white rounded-2xl shadow-lg overflow-x-auto mb-6">
                <div className="flex justify-center border-b border-gray-100 p-3 sm:hidden">
                    <div className="relative w-[340px] max-w-full">
                        <img src={ruloHesaplamaNew} alt="" className="block w-full h-auto" />
                        <span className="absolute left-[18%] top-[8%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                            {t.weight} [{weightUnit}]
                        </span>
                        <span className="absolute left-[-2%] top-[36%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                            {t.outerDiameter} [{lengthUnit}]
                        </span>
                        <span className="absolute left-[2%] top-[73%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                            {t.innerDiameter} [{lengthUnit}]
                        </span>
                        <span className="absolute left-[68%] top-[43%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                            {t.length} [{lengthUnit}]
                        </span>
                        <span className="absolute left-[72%] top-[94%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                            {t.width} [{lengthUnit}]
                        </span>
                        <span className="absolute left-[84%] top-[75%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                            {t.thickness} [{lengthUnit}]
                        </span>
                    </div>
                </div>
                <div className="min-w-[630px] sm:min-w-[1160px]">
                {/* Tablo Header */}
                <div className="grid grid-cols-[150px_repeat(4,minmax(120px,1fr))] bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200 sm:grid-cols-[360px_150px_repeat(4,minmax(120px,1fr))]">
                    <div className="p-4 sm:col-span-2"></div>
                    <div className="p-4 text-center font-bold text-gray-700 border-l border-gray-200">{t.single}</div>
                    <div className="p-4 text-center font-bold text-gray-700 border-l border-gray-200">{t.double}</div>
                    <div className="p-4 text-center font-bold text-gray-700 border-l border-gray-200">{t.triple}</div>
                    <div className="p-4 text-center font-bold text-gray-700 border-l border-gray-200">{t.quad}</div>
                </div>

                {/* Rulo Özellikleri Bölümü */}
                <div className="grid grid-cols-[150px_repeat(4,minmax(120px,1fr))] border-b-2 border-gray-200 sm:grid-cols-[360px_150px_repeat(4,minmax(120px,1fr))]">
                    {/* Sol görsel ve başlık - 6 satır boyunca */}
                    <div className="row-span-6 hidden p-3 items-center justify-center border-r border-gray-100 sm:flex">
                        <div className="relative w-[340px]">
                            <img src={ruloHesaplamaNew} alt="" className="block w-full h-auto" />
                            <span className="absolute left-[18%] top-[8%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                                {t.weight} [{weightUnit}]
                            </span>
                            <span className="absolute left-[-2%] top-[36%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                                {t.outerDiameter} [{lengthUnit}]
                            </span>
                            <span className="absolute left-[2%] top-[73%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                                {t.innerDiameter} [{lengthUnit}]
                            </span>
                            <span className="absolute left-[68%] top-[43%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                                {t.length} [{lengthUnit}]
                            </span>
                            <span className="absolute left-[72%] top-[94%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                                {t.width} [{lengthUnit}]
                            </span>
                            <span className="absolute left-[84%] top-[75%] whitespace-nowrap rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-bold text-gray-950 shadow-sm">
                                {t.thickness} [{lengthUnit}]
                            </span>
                        </div>
                    </div>

                    {[
                        { label: `${t.weight} (${weightUnit})`, vals: [calculations.tek.agirlik, calculations.cift.agirlik, calculations.uclu.agirlik, calculations.dortlu.agirlik].map(fromMetricWeight), dec: 2 },
                        { label: `OD (${lengthUnit})`, vals: [calculations.tek.OD, calculations.cift.OD, calculations.uclu.OD, calculations.dortlu.OD].map(fromMetricLength), dec: isImperial ? 3 : 1 },
                        { label: `ID (${lengthUnit})`, vals: [calculations.tek.Od, calculations.cift.Od, calculations.uclu.Od, calculations.dortlu.Od].map(fromMetricLength), dec: isImperial ? 3 : 0 },
                        { label: `W (${lengthUnit})`, vals: [calculations.tek.W, calculations.cift.W, calculations.uclu.W, calculations.dortlu.W].map(fromMetricLength), dec: isImperial ? 3 : 1 },
                        { label: `${t.pressTable.replace('(mm)', `(${lengthUnit})`)}`, vals: [calculations.tek.presTabla, calculations.cift.presTabla, calculations.uclu.presTabla, calculations.dortlu.presTabla].map(fromMetricLength), dec: isImperial ? 3 : 1 },
                        { label: `${t.totalLength.replace('(mm)', `(${lengthUnit})`)}`, vals: [calculations.tek.toplamBoy, calculations.cift.toplamBoy, calculations.uclu.toplamBoy, calculations.dortlu.toplamBoy].map(fromMetricLength), dec: isImperial ? 3 : 0 },
                    ].map((row, i, arr) => {
                        const rowBorder = i < arr.length - 1 ? 'border-b' : '';
                        return (
                            <React.Fragment key={row.label}>
                                <div className={`p-3 bg-gray-50 text-sm font-medium text-gray-600 flex items-center justify-center ${rowBorder} border-gray-100`}>{row.label}</div>
                                {row.vals.map((v, j) => (
                                    <div key={j} className={`p-3 font-semibold text-gray-800 border-l ${rowBorder} border-gray-100 flex items-center justify-center`}>{formatNumber(v, row.dec)}</div>
                                ))}
                            </React.Fragment>
                        );
                    })}
                </div>

                {/* Verim Bölümü */}
                <div className="grid grid-cols-[150px_repeat(4,minmax(120px,1fr))] bg-gradient-to-br from-emerald-50 to-teal-50 sm:grid-cols-[360px_150px_repeat(4,minmax(120px,1fr))]">
                    {/* Sol başlık - 3 satır boyunca */}
                    <div className="row-span-3 hidden p-4 items-center justify-center border-r border-gray-200 sm:flex">
                        <div className="text-center">
                            <div className="text-lg font-bold text-emerald-700">{t.efficiency}</div>
                            <div className="text-2xl font-bold text-emerald-600">%</div>
                        </div>
                    </div>

                    {/* Parça */}
                    <div className="p-3 bg-emerald-100/50 text-sm font-medium text-emerald-800 flex items-center justify-center border-b border-gray-200">{t.pieceKg}</div>
                    {[0, 1, 2, 3].map((j) => (
                        <div key={j} className="p-3 font-semibold text-gray-800 border-l border-b border-gray-200 flex items-center justify-center">{formatNumber(fromMetricWeight(calculations.parcaAgirlik), 4)}</div>
                    ))}

                    {/* Fire */}
                    <div className="p-3 bg-emerald-100/50 text-sm font-medium text-emerald-800 flex items-center justify-center border-b border-gray-200">{t.scrapKg}</div>
                    {[calculations.tek.fire, calculations.cift.fire, calculations.uclu.fire, calculations.dortlu.fire].map((v, j) => (
                        <div key={j} className="p-3 font-semibold text-red-600 border-l border-b border-gray-200 flex items-center justify-center">{formatNumber(fromMetricWeight(v), 4)}</div>
                    ))}

                    {/* Avantaj */}
                    <div className="p-3 bg-emerald-100/50 text-sm font-medium text-emerald-800 flex items-center justify-center">{t.advantage}</div>
                    <div className="p-3 font-bold text-gray-500 border-l border-gray-200 flex items-center justify-center">-</div>
                    {[calculations.cift.avantaj, calculations.uclu.avantaj, calculations.dortlu.avantaj].map((v, j) => (
                        <div key={j} className="p-3 font-bold text-emerald-600 border-l border-gray-200 text-lg flex items-center justify-center">{formatPercent(v)}</div>
                    ))}
                </div>
                </div>
            </div>

            {/* Dizilim Görseli */}
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
                <h3 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
                    <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                    {t.layoutVisual}
                </h3>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
                    {fixedLayouts.map((item, idx) => {
                        const isBest = item.key === bestLayoutKey;
                        return (
                            <div key={item.key} className="text-center">
                                <div className={`relative rounded-xl p-3 h-40 flex items-center justify-center sm:h-48 sm:p-4 ${
                                    isBest ? 'bg-emerald-50 ring-2 ring-emerald-400' : 'bg-gray-50'
                                }`}>
                                    {hasLayoutData && (
                                        <span className={`absolute top-2 left-2 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${
                                            isBest ? 'bg-emerald-500 text-white' : 'bg-gray-300 text-gray-700'
                                        }`}>
                                            {idx + 1}
                                        </span>
                                    )}
                                    {LAYOUT_SVGS[item.key]}
                                </div>
                                <p className={`mt-2 text-sm ${
                                    isBest ? 'font-bold text-emerald-600' : 'font-medium text-gray-600'
                                }`}>{item.label}</p>
                                {hasLayoutData && (
                                    <p className={`text-xs font-semibold ${
                                        isBest ? 'text-emerald-500' : 'text-gray-400'
                                    }`}>{formatPercent(item.avantaj)}</p>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ZigzagCalculator;
